<template>
    <div class="update-timesheets-root">
        <v-card id="details" :loading="loading" class="update-timesheets-card">
            <v-card-title ref="title" class="update-timesheets-toolbar">
                <v-btn
                    small
                    icon
                    @click="close"
                    color="primary"
                    class="mr-2"
                    :disabled="loading"
                    :loading="loading"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                <h3>{{ 'Update Timesheets' }}</h3>
                <v-spacer />
                <v-tooltip
                    v-if="hasIncompleteTimesheets"
                    bottom
                    max-width="300"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">
                            <v-btn
                                rounded
                                color="primary"
                                class="mx-2"
                                :disabled="loading || hasIncompleteTimesheets"
                                :loading="loading"
                                @click="saveTimesheets"
                                >SAVE</v-btn
                            >
                        </span>
                    </template>
                    <span>
                        Complete all clock-in times before saving.
                    </span>
                </v-tooltip>
                <v-btn
                    v-else
                    rounded
                    color="primary"
                    class="mx-2"
                    :disabled="loading"
                    :loading="loading"
                    @click="saveTimesheets"
                    >SAVE</v-btn
                >
            </v-card-title>

            <v-divider class="flex-grow-0" />

            <v-card-text class="update-timesheets-card-text">
                <v-alert
                    v-if="showClockInAlert"
                    type="warning"
                    dense
                    outlined
                    class="mb-3 flex-grow-0"
                >
                    <span v-if="hasRecordsWithDefaultClockIn">
                        Default 7:00 AM was applied to some records that were
                        missing clock-in (rows highlighted). You can change the
                        time if needed.
                    </span>
                    <span v-if="hasIncompleteTimesheets">
                        <template v-if="hasRecordsWithDefaultClockIn">
                            <br />
                        </template>
                        Some records are still missing clock-in. Enter the time
                        and save when complete. Clock-out is optional.
                    </span>
                </v-alert>
                <v-alert
                    v-if="unmatchedUsersCount"
                    type="error"
                    dense
                    text
                    class="mb-3 flex-grow-0"
                >
                    {{ unmatchedUsersCount }} user(s) without PA match
                    (highlighted). They will not save until matched.
                </v-alert>

                <div class="update-timesheets-search flex-grow-0">
                    <p class="text-h6 my-0 font-weight-bold">
                        USERS ({{ timesheetsCopy.length }})
                    </p>
                    <v-text-field
                        v-model="userSearch"
                        dense
                        filled
                        rounded
                        clearable
                        hide-details
                        prepend-inner-icon="mdi-magnify"
                        placeholder="Search by ID or name"
                        class="ma-0 update-timesheets-search-field"
                    />
                </div>

                <div ref="tableWrapper" class="update-timesheets-table-wrapper">
                    <v-data-table
                        :headers="headers"
                        :items="filteredTimesheetsCopy"
                        :items-per-page="-1"
                        :mobile-breakpoint="0"
                        :loading="loading"
                        single-expand
                        :expanded.sync="expanded"
                        item-key="rowKey"
                        show-expand
                        hide-default-footer
                        fixed-header
                        :height="tableHeight"
                        class="elevation-2 sticky-table"
                        :item-class="getUserRowClass"
                    >
                        <template v-slot:[`item.totalHours`]="{ item }">
                            <p class="my-0">
                                {{ getTotalHours(item) }}
                            </p>
                        </template>

                        <template
                            v-slot:expanded-item="{ headers, item: timesheet }"
                        >
                            <td
                                :colspan="headers.length"
                                class="py-4 ma-0 pl-10 pr-10 outlined expanded-row-content"
                                :style="{ background: 'white' }"
                            >
                                <div class="mb-4 expanded-inner-table-wrapper">
                                    <v-data-table
                                        :headers="timesheetsHeaders"
                                        :items="timesheet.timesheets"
                                        :item-class="getTimesheetRowClass"
                                        class="ma-0 pa-0 elevation-0 inner-timesheets-table"
                                        :mobile-breakpoint="0"
                                        :loading="loading"
                                        hide-default-footer
                                        item-key="tempId"
                                        style="width: auto;"
                                    >
                                        <template v-slot:top>
                                            <v-row
                                                no-gutter
                                                class="pa-0 ma-0"
                                                :style="{
                                                    'background-color':
                                                        '#eeeeee',
                                                }"
                                            >
                                                <v-col cols="12" class="d-flex">
                                                    <p
                                                        class="text-h5 my-0 pl-3 font-weight-bold mr-4 mt-1"
                                                    >
                                                        TIME RECORDS
                                                    </p>
                                                    <v-spacer />
                                                    <v-btn
                                                        rounded
                                                        color="primary"
                                                        class="mx-2 mb-2"
                                                        @click="
                                                            openUpdateScopes(
                                                                timesheet
                                                            )
                                                        "
                                                        >Update Scopes</v-btn
                                                    >
                                                </v-col></v-row
                                            >
                                        </template>
                                        <template
                                            v-slot:[`item.date`]="{ item }"
                                        >
                                            <p class="my-0">
                                                {{
                                                    item.date
                                                        ? formatDate(
                                                              item.date.seconds
                                                                  ? item.date
                                                                        .seconds
                                                                  : item.date
                                                                        ._seconds
                                                          )
                                                        : ''
                                                }}
                                            </p>
                                        </template>
                                        <template
                                            v-slot:[`item.day`]="{ item }"
                                        >
                                            <p class="my-0">
                                                {{
                                                    item.date
                                                        ? getDayOfWeek(
                                                              item.date.seconds
                                                                  ? item.date
                                                                        .seconds
                                                                  : item.date
                                                                        ._seconds
                                                          )
                                                        : ''
                                                }}
                                            </p>
                                        </template>
                                        <template
                                            v-slot:[`item.clockIn`]="{ item }"
                                        >
                                            <div
                                                class="d-flex justify-center align-center"
                                            >
                                                <v-text-field
                                                    :value="
                                                        getTimeString(
                                                            item.clockIn
                                                        )
                                                    "
                                                    type="time"
                                                    dense
                                                    hide-details
                                                    outlined
                                                    placeholder="--:--"
                                                    class="mt-0 pt-0"
                                                    style="max-width: 135px;"
                                                    :class="{
                                                        'error--text': !isValidTimestamp(
                                                            item.clockIn
                                                        ),
                                                    }"
                                                    @change="
                                                        v =>
                                                            setClockTime(
                                                                timesheet,
                                                                item,
                                                                'clockIn',
                                                                v
                                                            )
                                                    "
                                                />
                                            </div>
                                        </template>
                                        <template
                                            v-slot:[`item.clockOut`]="{ item }"
                                        >
                                            <div
                                                class="d-flex justify-center align-center"
                                            >
                                                <v-text-field
                                                    :value="
                                                        getTimeString(
                                                            item.clockOut
                                                        )
                                                    "
                                                    type="time"
                                                    dense
                                                    hide-details
                                                    outlined
                                                    placeholder="--:--"
                                                    class="mt-0 pt-0"
                                                    style="max-width: 135px;"
                                                    @change="
                                                        v =>
                                                            setClockTime(
                                                                timesheet,
                                                                item,
                                                                'clockOut',
                                                                v
                                                            )
                                                    "
                                                />
                                            </div>
                                        </template>
                                        <template
                                            v-slot:[`item.project`]="{ item }"
                                        >
                                            <p class="my-0">
                                                {{
                                                    item.projectId
                                                        ? getProjectName(
                                                              item.projectId
                                                          )
                                                        : ''
                                                }}
                                            </p>
                                        </template>
                                        <template
                                            v-slot:[`item.scope`]="{ item }"
                                        >
                                            <p class="my-0">
                                                {{
                                                    item.selectedCost?.cost
                                                        ?.reference || ''
                                                }}
                                            </p>
                                        </template>
                                    </v-data-table>
                                </div>
                            </td>
                        </template>
                    </v-data-table>
                </div>
            </v-card-text>

            <v-card-actions></v-card-actions>
        </v-card>
        <!--UPLOAD SCOPES-->
        <v-dialog v-model="openUpdateScopesDialog" persistent max-width="800">
            <v-card :loading="loading">
                <v-card-title>
                    <v-btn
                        class="mr-3"
                        depressed
                        color="primary"
                        icon
                        rounded
                        :loading="loading"
                        @click="closeUpdateScopes"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    Select the records to assign a scope
                    <v-spacer></v-spacer>
                </v-card-title>
                <v-divider class="ml-4 mr-5" />
                <v-card-text>
                    <v-data-table
                        :headers="headersForScopes"
                        :items="timesheetsForScopes"
                        :loading="loading"
                        class="elevation-1 mt-4"
                        disable-pagination
                        hide-default-footer
                    >
                        <template v-slot:[`item.selected`]="{ item }">
                            <div class="d-flex align-center justify-center">
                                <v-checkbox
                                    v-model="item.isSelected"
                                    :input-value="item.isSelected"
                                    dense
                                    hide-details
                                ></v-checkbox>
                            </div>
                        </template>
                        <template v-slot:[`item.date`]="{ item }">
                            <p class="my-0">
                                {{
                                    item.date
                                        ? formatDate(
                                              item.date.seconds
                                                  ? item.date.seconds
                                                  : item.date._seconds
                                          )
                                        : ''
                                }}
                            </p>
                        </template>
                        <template v-slot:[`item.day`]="{ item }">
                            <p class="my-0">
                                {{
                                    item.date
                                        ? getDayOfWeek(
                                              item.date.seconds
                                                  ? item.date.seconds
                                                  : item.date._seconds
                                          )
                                        : ''
                                }}
                            </p>
                        </template>
                        <template v-slot:[`item.project`]="{ item }">
                            <p class="my-0">
                                {{
                                    item.projectId
                                        ? getProjectName(item.projectId)
                                        : ''
                                }}
                            </p>
                        </template>
                    </v-data-table>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="primary"
                        rounded
                        :loading="loading"
                        class="mb-3"
                        @click="openScopesSelect"
                        :disabled="allUnselected"
                    >
                        NEXT
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!--UPLOAD Project-->
        <v-dialog v-model="openDialogProjects" persistent max-width="400">
            <v-card :loading="loading">
                <v-card-title>
                    <v-btn
                        class="mr-3"
                        depressed
                        color="primary"
                        icon
                        rounded
                        :loading="loading"
                        @click="closeDialogProjects"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    Select a project
                    <v-spacer></v-spacer>
                </v-card-title>
                <v-divider />
                <v-card-text>
                    <v-row>
                        <v-col cols="12" md="12" sm="12" lg="12">
                            <v-autocomplete
                                v-model="selectedProject"
                                :items="filteredProjects"
                                item-text="name"
                                item-value="id"
                                hide-details
                                prepend-icon="mdi-clipboard-check"
                                placeholder="Select project"
                            />
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="primary"
                        rounded
                        :loading="loading"
                        class="mb-3"
                        @click="showProjectsTimesheets"
                        :disabled="!(selectedProject != null)"
                    >
                        NEXT
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!--UPLOAD FILE-->
        <v-dialog v-model="openDialogScopes" persistent max-width="400">
            <v-card :loading="loading">
                <v-card-title>
                    <v-btn
                        class="mr-3"
                        depressed
                        color="primary"
                        icon
                        rounded
                        :loading="loading"
                        @click="closeScopesSelect"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    Select a scope
                    <v-spacer></v-spacer>
                </v-card-title>
                <v-divider class="ml-4 mr-5" />
                <v-card-text>
                    <v-row>
                        <v-col cols="12" md="12" sm="12" lg="12">
                            <v-autocomplete
                                v-model="selectedQuote"
                                :items="quotesFromProject"
                                item-text="name"
                                return-object
                                hide-details
                                placeholder="Select a quote"
                                prepend-icon="mdi-invoice"
                                @change="loadScopesFromQuote()"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" md="12" sm="12" lg="12">
                            <v-autocomplete
                                v-model="selectedScope"
                                :items="scopesFromQuote"
                                item-text="reference"
                                return-object
                                hide-details
                                placeholder="Select a scope"
                                prepend-icon="mdi-tools"
                                :disabled="selectedQuote === null"
                            />
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="primary"
                        rounded
                        :loading="loading"
                        @click="saveScopes"
                        :disabled="
                            selectedQuote === null || selectedScope === null
                        "
                        class="mb-3"
                    >
                        SAVE
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import moment from 'moment'
import _ from 'lodash'
import API from '@/services/api'

export default {
    name: 'UpdateTimesheetsForm',

    props: {
        externalUsers: {
            type: Array,
            default: () => [],
        },
        timesheets: {
            type: Array,
            default: () => [],
        },
        projectsLocations: {
            type: Array,
            default: () => [],
        },
        projects: {
            type: Array,
            default: () => [],
        },
    },

    data: () => ({
        openDialogProjects: false,
        valid: false,
        selectedProject: null,
        loading: false,
        userSearch: '',
        tableHeight: 400,
        companyId: JSON.parse(localStorage.getItem('company')),
        headers: [
            {
                text: 'ID',
                value: 'siaId',
                sortable: true,
                align: 'center',
                width: '100px',
            },
            {
                text: 'NAME',
                value: 'name',
                sortable: true,
                align: 'center',
                width: '100px',
            },
            {
                text: 'Total Hours',
                value: 'totalHours',
                sortable: false,
                align: 'center',
                width: '100px',
            },
        ],
        timesheetsHeaders: [
            {
                text: 'DATE',
                value: 'date',
                sortable: false,
                align: 'center',
                width: '100px',
            },
            {
                text: 'DAY',
                value: 'day',
                sortable: false,
                align: 'center',
                width: '100px',
            },
            {
                text: 'CLOCK-IN',
                value: 'clockIn',
                sortable: false,
                align: 'center',
                width: '100px',
            },
            {
                text: 'CLOCK-OUT',
                value: 'clockOut',
                sortable: false,
                align: 'center',
                width: '100px',
            },
            {
                text: 'PROJECT',
                value: 'project',
                sortable: false,
                align: 'center',
                width: '100px',
            },
            {
                text: 'SCOPE',
                value: 'scope',
                sortable: false,
                align: 'center',
                width: '100px',
            },
        ],
        headersForScopes: [
            {
                text: 'SELECT',
                value: 'selected',
                sortable: false,
                align: 'center',
                width: '50px',
            },
            {
                text: 'DATE',
                value: 'date',
                sortable: false,
                align: 'center',
                width: '150px',
            },
            {
                text: 'DAY',
                value: 'day',
                sortable: false,
                align: 'center',
                width: '100px',
            },
            {
                text: 'PROJECT',
                value: 'project',
                sortable: false,
                align: 'center',
            },
        ],
        expanded: [],
        openUpdateScopesDialog: false,
        timesheetsForScopes: [],
        selectedCost: {},
        openDialogScopes: false,
        timesheetsCopy: [],
        savingInProgress: false,
        filteredProjects: [],
        quotesFromProject: [],
        scopesFromQuote: [],
        selectedQuote: null,
        selectedScope: null,
        userSelected: {},
    }),
    computed: {
        allUnselected() {
            return !this.timesheetsForScopes.some(item => item.isSelected)
        },
        hasIncompleteTimesheets() {
            if (!this.timesheetsCopy || !this.timesheetsCopy.length)
                return false
            return this.timesheetsCopy.some(person =>
                (person.timesheets || []).some(
                    ts => !this.isValidTimestamp(ts.clockIn)
                )
            )
        },
        hasRecordsWithDefaultClockIn() {
            if (!this.timesheetsCopy || !this.timesheetsCopy.length)
                return false
            return this.timesheetsCopy.some(person =>
                (person.timesheets || []).some(
                    ts => ts.clockInWasDefault === true
                )
            )
        },
        showClockInAlert() {
            return (
                this.hasIncompleteTimesheets ||
                this.hasRecordsWithDefaultClockIn
            )
        },
        unmatchedUsersCount() {
            return (this.timesheetsCopy || []).filter(t => !t.externalUserId)
                .length
        },
        filteredTimesheetsCopy() {
            const list = [...(this.timesheetsCopy || [])].sort((a, b) => {
                const aId = String(a.siaId || '')
                const bId = String(b.siaId || '')
                return aId.localeCompare(bId, undefined, {
                    numeric: true,
                    sensitivity: 'base',
                })
            })
            const q = String(this.userSearch || '')
                .trim()
                .toLowerCase()
            if (!q) return list
            return list.filter(person => {
                const sia = String(person.siaId || '').toLowerCase()
                const name = String(person.name || '').toLowerCase()
                const last = String(person.lastName || '').toLowerCase()
                return (
                    sia.includes(q) ||
                    name.includes(q) ||
                    last.includes(q) ||
                    `${name} ${last}`.includes(q)
                )
            })
        },
    },
    watch: {
        showClockInAlert() {
            this.scheduleTableHeightUpdate()
        },
        unmatchedUsersCount() {
            this.scheduleTableHeightUpdate()
        },
        userSearch() {
            this.scheduleTableHeightUpdate()
        },
        loading(val) {
            if (!val) this.scheduleTableHeightUpdate()
        },
    },

    async mounted() {
        try {
            this.loading = true
            window.addEventListener('resize', this.updateTableHeight)
            this.timesheetsCopy = _.cloneDeep(this.timesheets)
            this.timesheetsCopy.forEach(person => {
                const list = person.timesheets || []
                list.forEach(ts => {
                    if (!this.isValidTimestamp(ts.clockIn) && ts.date) {
                        const sec = ts.date._seconds ?? ts.date.seconds
                        if (sec != null) {
                            const baseDate = new Date(sec * 1000)
                            baseDate.setHours(7, 0, 0, 0)
                            this.$set(ts, 'clockIn', {
                                _seconds: Math.floor(baseDate.getTime() / 1000),
                                _nanoseconds: 0,
                            })
                            this.$set(ts, 'clockInWasDefault', true)
                        }
                    }
                })
            })
            const normalizeName = (name, lastName = '') =>
                `${name || ''} ${lastName || ''}`
                    .trim()
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/\s+/g, ' ')

            const userMap = new Map()
            const userByName = new Map()
            this.externalUsers.forEach(user => {
                const siaKey =
                    user.siaId != null ? String(user.siaId).trim() : null
                const value = { id: user.id, name: user.name }
                if (siaKey) {
                    userMap.set(siaKey, value)
                    const stripped = siaKey.replace(/^0+/, '') || '0'
                    if (!userMap.has(stripped)) userMap.set(stripped, value)
                }
                const nameKey = normalizeName(user.name)
                if (nameKey) userByName.set(nameKey, value)
            })
            const findUserBySia = siaId => {
                const key = siaId != null ? String(siaId).trim() : null
                if (!key) return null
                if (userMap.has(key)) return userMap.get(key)
                const stripped = key.replace(/^0+/, '') || '0'
                return userMap.get(stripped) || null
            }
            const findUserByName = (name, lastName) => {
                const excelName = normalizeName(name, lastName)
                if (!excelName) return null
                if (userByName.has(excelName)) return userByName.get(excelName)
                for (const [mapKey, value] of userByName.entries()) {
                    if (
                        mapKey.includes(excelName) ||
                        excelName.includes(mapKey)
                    ) {
                        return value
                    }
                }
                return null
            }

            this.timesheetsCopy = this.timesheetsCopy.map((ts, index) => {
                const siaKey = ts.siaId != null ? String(ts.siaId).trim() : null
                const matched =
                    findUserBySia(siaKey) ||
                    findUserByName(ts.name, ts.lastName)
                return {
                    ...ts,
                    siaId: siaKey,
                    externalUserId: matched?.id || null,
                    name: matched?.name || ts.name,
                    rowKey:
                        matched?.id ||
                        siaKey ||
                        `unmatched-${index}-${ts.name || 'user'}`,
                }
            })
            const unmatched = this.timesheetsCopy.filter(t => !t.externalUserId)
            if (unmatched.length) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: `Unmatched users (will not save): ${unmatched
                        .map(u => `${u.name || ''} (SIA ${u.siaId || 'N/A'})`)
                        .join(', ')}`,
                })
            }
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
            this.scheduleTableHeightUpdate()
        }
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateTableHeight)
        if (this._tableHeightTimer) {
            clearTimeout(this._tableHeightTimer)
        }
    },

    methods: {
        ...mapMutations(['setErrorItems']),

        close() {
            this.$emit('close')
        },
        scheduleTableHeightUpdate() {
            this.$nextTick(() => {
                if (this._tableHeightTimer) {
                    clearTimeout(this._tableHeightTimer)
                }
                this._tableHeightTimer = setTimeout(() => {
                    this.updateTableHeight()
                }, 50)
            })
        },
        updateTableHeight() {
            const wrapper = this.$refs.tableWrapper
            if (!wrapper) return
            const nextHeight = Math.max(240, wrapper.clientHeight)
            if (Math.abs(nextHeight - this.tableHeight) > 2) {
                this.tableHeight = nextHeight
            }
        },
        getUserRowClass(item) {
            return !item.externalUserId ? 'row-unmatched-user' : ''
        },
        isValidTimestamp(ts) {
            if (!ts || typeof ts !== 'object') return false
            const sec = ts._seconds ?? ts.seconds
            return sec != null
        },
        getTimeString(ts) {
            if (!this.isValidTimestamp(ts)) return ''
            const sec = ts.seconds != null ? ts.seconds : ts._seconds || 0
            return moment.unix(sec).format('HH:mm')
        },
        /**
         * Convierte clockIn/clockOut a objeto plano { _seconds, _nanoseconds } para la API.
         * Acepta objeto Firestore-like (_seconds/.seconds) o string "HH:mm" con dateObj.
         */
        normalizeTimestampForApi(val, dateObj) {
            if (!val) return null
            const sec =
                typeof val === 'object' ? val._seconds ?? val.seconds : null
            if (sec != null) {
                const nan = val._nanoseconds ?? val.nanoseconds ?? 0
                return { _seconds: sec, _nanoseconds: nan }
            }
            if (typeof val === 'string' && dateObj) {
                const dateSec = dateObj._seconds ?? dateObj.seconds
                if (dateSec == null) return null
                const [hh, mm, ss = 0] = val.split(':').map(Number)
                const baseDate = new Date(dateSec * 1000)
                baseDate.setHours(hh || 0, mm || 0, ss || 0, 0)
                return {
                    _seconds: Math.floor(baseDate.getTime() / 1000),
                    _nanoseconds: 0,
                }
            }
            return null
        },
        setClockTime(personRow, item, field, timeStr) {
            if (!timeStr || !item.date) return
            const sec = item.date._seconds || item.date.seconds
            if (sec == null) return
            const baseDate = new Date(sec * 1000)
            const [hh, mm] = timeStr.split(':').map(Number)
            baseDate.setHours(hh || 0, mm || 0, 0, 0)
            const newSeconds = Math.floor(baseDate.getTime() / 1000)
            const currentVal =
                field === 'clockIn' ? item.clockIn : item.clockOut
            const currentSeconds = currentVal?._seconds ?? currentVal?.seconds
            if (currentSeconds === newSeconds) return
            const timestamp = {
                _seconds: newSeconds,
                _nanoseconds: 0,
            }
            const personIndex = this.timesheetsCopy.findIndex(
                p => p.externalUserId === personRow.externalUserId
            )
            if (personIndex === -1) return
            const tsIndex = (
                this.timesheetsCopy[personIndex].timesheets || []
            ).findIndex(t => t.tempId === item.tempId)
            if (tsIndex === -1) return
            this.$set(
                this.timesheetsCopy[personIndex].timesheets[tsIndex],
                field,
                timestamp
            )
        },
        getTotalHours(item) {
            if (!item.timesheets || !Array.isArray(item.timesheets)) return 0

            const totalHours = item.timesheets.reduce((total, ts) => {
                const clockInSec = ts.clockIn?._seconds
                const clockOutSec = ts.clockOut?._seconds

                if (!clockInSec || !clockOutSec) return total

                const diffMs = (clockOutSec - clockInSec) * 1000
                const diffHours = diffMs / (1000 * 60 * 60)

                return total + diffHours
            }, 0)

            return totalHours.toFixed(2)
        },
        openUpdateScopes(item) {
            this.userSelected = item
            this.timesheetsForScopes = item.timesheets
            this.timesheetsForScopes = this.timesheetsForScopes.map(item => ({
                ...item,
                isSelected: false,
            }))
            const uniqueProjectIds = [
                ...new Set(
                    this.timesheetsForScopes
                        .map(ts => ts.projectId)
                        .filter(id => !!id)
                ),
            ]
            this.filteredProjects = this.projects.filter(project =>
                uniqueProjectIds.includes(project.id)
            )
            this.openDialogProjects = true
        },
        saveScopes() {
            try {
                this.loading = true
                const selected = this.timesheetsForScopes.filter(
                    item => item.isSelected
                )
                const found = this.timesheetsCopy.find(
                    copy =>
                        copy.externalUserId === this.userSelected.externalUserId
                )
                if (found) {
                    const tempIds = [...new Set(selected.map(i => i.tempId))]
                    ;(found.timesheets || []).forEach(ts => {
                        if (tempIds.includes(ts.tempId)) {
                            ts.selectedCost = {
                                cost: {
                                    id: this.selectedScope.id,
                                    reference: this.selectedScope.reference,
                                    selected: true,
                                },
                                quote: {
                                    id: this.selectedQuote.id,
                                    name: this.selectedQuote.name,
                                },
                            }
                        }
                    })
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.closeScopesSelect()
                this.loading = false
            }
        },
        showProjectsTimesheets() {
            this.openDialogProjects = false
            this.timesheetsForScopes = this.timesheetsForScopes.filter(
                ts => ts.projectId === this.selectedProject
            )
            this.openUpdateScopesDialog = true
        },
        closeDialogProjects() {
            this.openDialogProjects = false
            this.selectedProject = null
            this.userSelected = {}
            this.timesheetsForScopes = []
        },
        closeUpdateScopes() {
            this.openUpdateScopesDialog = false
            this.closeDialogProjects()
        },
        closeScopesSelect() {
            this.openDialogScopes = false
            this.selectedQuote = null
            this.selectedScope = null
            this.closeUpdateScopes()
        },
        async openScopesSelect() {
            try {
                this.loading = true
                this.quotesFromProject = await API.getProjectQuotes(
                    this.selectedProject
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.openUpdateScopesDialog = false
                this.openDialogScopes = true
            }
        },
        async loadScopesFromQuote() {
            try {
                this.loading = true
                this.selectedScope = null
                this.scopesFromQuote = await API.getCosts(this.selectedQuote.id)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        formatDate(seconds) {
            return moment.unix(seconds).format('YYYY-MM-DD')
        },
        getDayOfWeek(seconds) {
            const date = new Date(seconds * 1000)
            return new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(
                date
            )
        },
        formatTime(seconds) {
            return moment.unix(seconds).format('hh:mm A')
        },
        getProjectName(id) {
            if (this.projects.length > 0 && id) {
                return this.projects.find(project => project.id == id)?.name
            } else {
                return ''
            }
        },
        getTimesheetRowClass(item) {
            return item.clockInWasDefault ? 'row-default-clock-in' : ''
        },
        async saveTimesheets() {
            if (this.hasIncompleteTimesheets) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: 'Complete all clock-in times before saving.',
                })
                return
            }
            try {
                this.loading = true
                this.savingInProgress = true
                const flattened = this.timesheetsCopy.flatMap(person =>
                    (person.timesheets || []).map(ts => {
                        const timestamp = ts.date?._seconds
                            ? new Date(
                                  ts.date._seconds * 1000 +
                                      (ts.date._nanoseconds || 0) / 1e6
                              )
                            : null

                        const formattedDate = timestamp
                            ? (() => {
                                  const y = timestamp.getFullYear()
                                  const m = String(
                                      timestamp.getMonth() + 1
                                  ).padStart(2, '0')
                                  const d = String(
                                      timestamp.getDate()
                                  ).padStart(2, '0')
                                  return `${y}-${m}-${d}`
                              })()
                            : null

                        const pl = ts.projectLocation
                            ? {
                                  createdBy: ts.projectLocation.createdBy,
                                  createdOn: ts.projectLocation.createdOn,
                                  id: ts.projectLocation.id,
                                  location: ts.projectLocation.location,
                                  name: ts.projectLocation.name,
                                  notes: ts.projectLocation.notes,
                                  projectId: ts.projectLocation.projectId,
                              }
                            : null

                        const clockInPayload = this.normalizeTimestampForApi(
                            ts.clockIn,
                            ts.date
                        )
                        const clockOutPayload = this.normalizeTimestampForApi(
                            ts.clockOut,
                            ts.date
                        )

                        return {
                            personName: person.name,
                            siaId: person.siaId,
                            externalUserId: person.externalUserId,
                            clockIn: clockInPayload,
                            clockInLocation: ts.clockInLocation,
                            ...(clockOutPayload
                                ? {
                                      clockOut: clockOutPayload,
                                      clockOutLocation: ts.clockOutLocation,
                                  }
                                : {
                                      clockOutLocation: ts.clockOutLocation,
                                  }),
                            date: formattedDate,
                            projectId: ts.projectId,
                            projectLocation: pl,
                            ...(ts.selectedCost
                                ? { selectedCost: ts.selectedCost }
                                : {}),
                        }
                    })
                )

                const invalidReasons = []
                const validTimesheets = []
                for (const obj of flattened) {
                    const label = `${obj.personName ||
                        'Unknown'} (SIA ${obj.siaId || 'N/A'}) ${obj.date ||
                        'no-date'}`
                    if (!obj.externalUserId) {
                        invalidReasons.push(
                            `${label}: missing user match (SIA)`
                        )
                        continue
                    }
                    if (!obj.projectId || String(obj.projectId).trim() === '') {
                        invalidReasons.push(`${label}: missing project`)
                        continue
                    }
                    if (!obj.projectLocation || !obj.projectLocation.id) {
                        invalidReasons.push(
                            `${label}: missing project location`
                        )
                        continue
                    }
                    if (
                        !obj.clockIn ||
                        typeof obj.clockIn !== 'object' ||
                        (obj.clockIn._seconds == null &&
                            obj.clockIn.seconds == null)
                    ) {
                        invalidReasons.push(`${label}: missing clock-in`)
                        continue
                    }
                    if (!obj.date) {
                        invalidReasons.push(`${label}: missing date`)
                        continue
                    }
                    validTimesheets.push({
                        externalUserId: obj.externalUserId,
                        clockIn: obj.clockIn,
                        clockInLocation: obj.clockInLocation,
                        ...(obj.clockOut
                            ? {
                                  clockOut: obj.clockOut,
                                  clockOutLocation: obj.clockOutLocation,
                              }
                            : {
                                  clockOutLocation: obj.clockOutLocation,
                              }),
                        date: obj.date,
                        projectId: obj.projectId,
                        projectLocation: obj.projectLocation,
                        ...(obj.selectedCost
                            ? { selectedCost: obj.selectedCost }
                            : {}),
                    })
                }

                if (invalidReasons.length && !validTimesheets.length) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: `Cannot save: ${
                            invalidReasons.length
                        } invalid row(s). ${invalidReasons
                            .slice(0, 5)
                            .join('; ')}${
                            invalidReasons.length > 5 ? '…' : ''
                        }`,
                    })
                    return
                }

                if (!validTimesheets.length) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: 'No valid timesheets to save.',
                    })
                    return
                }

                let created = 0
                const failed = []
                const BATCH_SIZE = 200
                for (let i = 0; i < validTimesheets.length; i += BATCH_SIZE) {
                    const chunk = validTimesheets.slice(i, i + BATCH_SIZE)
                    try {
                        const result = await API.createTimesheetsBatch({
                            timesheets: chunk,
                        })
                        created += result.created || 0
                        ;(result.failed || []).forEach(f => {
                            const row = chunk[f.index] || {}
                            failed.push(
                                `${row.date || 'row'}: ${f.message || 'failed'}`
                            )
                        })
                    } catch {
                        const settled = await Promise.allSettled(
                            chunk.map(obj => API.createTimesheet(obj))
                        )
                        settled.forEach((res, idx) => {
                            if (res.status === 'fulfilled') {
                                created += 1
                            } else {
                                const reason = res.reason
                                failed.push(
                                    `${chunk[idx].date ||
                                        'row'}: ${reason?.message || reason}`
                                )
                            }
                        })
                    }
                }

                if (failed.length || invalidReasons.length) {
                    const parts = []
                    if (created) {
                        parts.push(`Saved ${created}/${validTimesheets.length}`)
                    }
                    if (invalidReasons.length) {
                        parts.push(
                            `Skipped ${
                                invalidReasons.length
                            }: ${invalidReasons.slice(0, 3).join('; ')}`
                        )
                    }
                    if (failed.length) {
                        parts.push(`Failures: ${failed.slice(0, 3).join('; ')}`)
                    }
                    this.setErrorItems({
                        source: this.$options.name,
                        message: parts.join('. '),
                    })
                    if (created && !failed.length) {
                        this.close()
                    }
                    return
                }

                this.close()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.savingInProgress = false
            }
        },
    },
}
</script>
<style>
.update-timesheets-dialog {
    height: 100% !important;
    max-height: 100vh !important;
    margin: 0 !important;
    overflow: hidden !important;
}

.update-timesheets-dialog > .update-timesheets-root {
    height: 100%;
    max-height: 100vh;
}

.update-timesheets-root {
    height: 100%;
    max-height: 100vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #fff;
}

.update-timesheets-card {
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden;
}

.update-timesheets-toolbar {
    flex: 0 0 auto;
    background-color: #fff;
    z-index: 2;
}

.update-timesheets-card-text {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    max-height: 100%;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
    padding-top: 12px !important;
    padding-bottom: 12px !important;
}

.update-timesheets-search {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 12px;
    padding: 8px 12px;
    background: #eeeeee;
    border-radius: 4px;
}

.update-timesheets-search-field {
    flex: 1 1 280px;
    max-width: 480px;
}

.update-timesheets-table-wrapper {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    position: relative;
}

.update-timesheets-table-wrapper .sticky-table {
    height: 100%;
}

.update-timesheets-table-wrapper .sticky-table .v-data-table__wrapper {
    overflow-y: auto !important;
}

.update-timesheets-table-wrapper
    .sticky-table
    .v-data-table__wrapper
    table
    thead
    th {
    background-color: #eeeeee !important;
}

.update-timesheets-table-wrapper .sticky-table .expanded-row-content {
    position: relative;
    z-index: 0;
}

.update-timesheets-table-wrapper
    .sticky-table
    .expanded-row-content
    .expanded-inner-table-wrapper,
.update-timesheets-table-wrapper
    .sticky-table
    .expanded-row-content
    .inner-timesheets-table,
.update-timesheets-table-wrapper
    .sticky-table
    .expanded-row-content
    .inner-timesheets-table
    .v-data-table__wrapper {
    position: relative;
    z-index: 0;
}

.row-default-clock-in {
    background-color: rgba(255, 152, 0, 0.15) !important;
}

.row-unmatched-user {
    background-color: rgba(244, 67, 54, 0.12) !important;
}
</style>
